/* Phase 287.3 — Job Reopen Audit
 *
 * Admin-only report that lists every job-completion reversal ("Uncomplete")
 * in the last N days. Useful guardrail to spot accidental completion patterns
 * or training opportunities once the team scales.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowCounterClockwise, ArrowRight, ChartBar, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  cancelled: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
};

export default function JobReopenAuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/jobs/reopen-audit?days=${days}&limit=500`);
      setItems(data.items || []);
      setTotal(data.total_reopen_events || 0);
    } catch {
      toast.error("Failed to load reopen audit");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Per-user breakdown (top 5 reopeners)
  const byUser = items.reduce((acc, it) => {
    const name = it.reopened_by_name || "—";
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const topUsers = Object.entries(byUser).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.job_reference || "").toLowerCase().includes(q) ||
      (it.job_title || "").toLowerCase().includes(q) ||
      (it.client_name || "").toLowerCase().includes(q) ||
      (it.reopened_by_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="job-reopen-audit-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <ArrowCounterClockwise size={18} className="text-white" weight="bold" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Job Reopen Audit</h1>
          <p className="text-xs text-muted-foreground">Every time a completed job was reverted, with who and when.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="w-[140px]" data-testid="audit-days-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="border border-border rounded-lg p-3 bg-card" data-testid="kpi-total">
          <ChartBar size={16} className="text-amber-500" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo] mt-1">{total}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total reopen events</p>
        </div>
        <div className="border border-border rounded-lg p-3 bg-card" data-testid="kpi-unique-jobs">
          <ChartBar size={16} className="text-blue-500" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo] mt-1">{new Set(items.map(i => i.job_id)).size}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Unique jobs</p>
        </div>
        <div className="border border-border rounded-lg p-3 bg-card" data-testid="kpi-unique-users">
          <ChartBar size={16} className="text-violet-500" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo] mt-1">{Object.keys(byUser).length}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Distinct admins</p>
        </div>
        <div className="border border-border rounded-lg p-3 bg-card" data-testid="kpi-top-user">
          <ChartBar size={16} className="text-rose-500" weight="duotone" />
          <p className="text-sm font-semibold mt-1 truncate" title={topUsers[0]?.[0] || "—"}>{topUsers[0]?.[0] || "—"}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Top reopener · {topUsers[0]?.[1] || 0}×</p>
        </div>
      </div>

      {/* Top reopeners list */}
      {topUsers.length > 0 && (
        <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="top-reopeners">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Top reopeners</p>
          <div className="flex flex-wrap gap-2">
            {topUsers.map(([name, count]) => (
              <div key={name} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs" data-testid={`top-user-${name}`}>
                <span className="font-medium">{name}</span>
                <span className="text-muted-foreground">×{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by job reference, title, client, or admin name..."
          className="pl-8 h-9 text-sm"
          data-testid="audit-search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-12 text-center" data-testid="audit-empty">
          <ArrowCounterClockwise size={28} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No reopen events in this period</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Your team&apos;s completion discipline is on point.</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="audit-table">
          <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold border-b border-border bg-muted/30">
            <div className="col-span-3">Job</div>
            <div className="col-span-2">Client</div>
            <div className="col-span-2">Reopened by</div>
            <div className="col-span-2">When</div>
            <div className="col-span-2">Now</div>
            <div className="col-span-1 text-right">View</div>
          </div>
          <ul className="divide-y divide-border">
            {filtered.map((it, i) => (
              <li key={`${it.job_id}-${it.reopened_at}-${i}`} className="grid grid-cols-12 px-4 py-3 items-center hover:bg-muted/30 transition-colors" data-testid={`audit-row-${it.job_id}-${i}`}>
                <div className="col-span-3 min-w-0">
                  <p className="text-sm font-medium truncate">{it.job_title || "—"}</p>
                  <p className="text-[10px] text-muted-foreground">{it.job_reference}</p>
                </div>
                <div className="col-span-2 text-xs text-muted-foreground truncate">{it.client_name || "—"}</div>
                <div className="col-span-2 min-w-0">
                  <p className="text-xs font-medium truncate">{it.reopened_by_name}</p>
                  {it.reopened_by_role && (
                    <p className="text-[10px] text-muted-foreground capitalize">{it.reopened_by_role.replace("_", " ")}</p>
                  )}
                </div>
                <div className="col-span-2 text-xs text-muted-foreground" title={it.reopened_at}>
                  {it.reopened_at ? new Date(it.reopened_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  <p className="text-[10px] opacity-70">
                    {it.reopened_at ? new Date(it.reopened_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </p>
                </div>
                <div className="col-span-2">
                  <Badge className={`text-[10px] ${STATUS_COLORS[it.current_status] || ""}`} variant="outline">
                    {(it.current_status || "—").replace("_", " ")}
                  </Badge>
                </div>
                <div className="col-span-1 text-right">
                  <Link to={`/jobs/${it.job_id}`}>
                    <Button variant="ghost" size="sm" className="h-7 text-[10px]" data-testid={`audit-view-${it.job_id}`}>
                      <ArrowRight size={12} />
                    </Button>
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
